import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../../config/apiRouting';
import authenticatedFetch from '../../utils/authenticatedFetch';
import './WeatherDashboard.css';

interface WeatherRecord {
  weatherId: string;
  storeId: string;
  storeName: string;
  date: string;
  daypart: string;
  daypartKey: string;
  location: {
    latitude: number;
    longitude: number;
    address: string;
    city: string;
    state: string;
    zipCode: string;
  };
  weather: {
    temperature: number;
    weatherText: string;
    weatherIcon: number;
    isDayTime: boolean;
    precipitationProbability: number;
    precipitationAmount: number;
    humidity: number;
    windSpeed: number;
    windDirection: number;
    uvIndex: number;
    visibility: number;
  };
  dailyForecast: {
    highTemperature: number;
    lowTemperature: number;
    dayWeatherText: string;
    nightWeatherText: string;
    precipitationProbability: number;
    precipitationAmount: number;
  };
  metadata: {
    collectionTimestamp: string;
    source: string;
    locationKey: string;
    dataQuality: string;
  };
}

interface WeatherTrends {
  summary: string;
  weatherPatterns: Array<{
    condition: string;
    count: number;
    percentage: string;
  }>;
  temperatureAnalysis: {
    average: number;
    maximum: number;
    minimum: number;
    range: number;
  };
  precipitationAnalysis: {
    totalDaysWithPrecipitation: number;
    precipitationPercentage: string;
    averagePrecipitationAmount: string;
  };
}

const WeatherDashboard: React.FC = () => {
  const [weatherData, setWeatherData] = useState<WeatherRecord[]>([]);
  const [trends, setTrends] = useState<WeatherTrends | null>(null);
  const [stores, setStores] = useState<any[]>([]);
  const [dayparts, setDayparts] = useState<Array<{ key: string; name: string; startTime?: string; endTime?: string }>>([]);
  const [fullDaypartConfig, setFullDaypartConfig] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [selectedStore, setSelectedStore] = useState('');
  const [selectedDaypart, setSelectedDaypart] = useState('');
  const [dateRange, setDateRange] = useState(30);
  const [activeTab, setActiveTab] = useState<'data' | 'trends' | 'collect'>('data');

  useEffect(() => {
    loadStores();
    loadWeatherData();
    loadDayparts();
  }, []);

  useEffect(() => {
    if (activeTab === 'data') {
      loadWeatherData();
    } else if (activeTab === 'trends') {
      loadTrends();
    }
  }, [selectedStore, selectedDaypart, dateRange, activeTab]);

  const loadStores = async () => {
    try {
      console.log('🏪 Loading stores from:', API_ENDPOINTS.stores());
      const response = await authenticatedFetch(API_ENDPOINTS.stores());
      const data = await response.json();
      console.log('🏪 Stores API response:', data);
      // Handle both array response and wrapped response formats
      if (Array.isArray(data)) {
        setStores(data);
        console.log('✅ Loaded stores (array):', data.length);
      } else if (data.success) {
        setStores(data.data || []);
        console.log('✅ Loaded stores:', data.data?.length || 0);
      } else if (data.length !== undefined) {
        // Direct array response
        setStores(data);
        console.log('✅ Loaded stores:', data.length);
      } else {
        console.error('❌ Stores API failed:', data);
      }
    } catch (err) {
      console.error('Error loading stores:', err);
    }
  };

  const loadDayparts = async () => {
    try {
      // For now, we'll use a default enterprise ID or load from the first store's enterprise
      // In a full implementation, this should be based on user's enterprise context
      const defaultEnterpriseId = '17527771077049luj0w1ek'; // Big Demo Enterprise One

      const response = await authenticatedFetch(API_ENDPOINTS.enterpriseDayparts(defaultEnterpriseId));
      const data = await response.json();
      
      if (data.success && data.data?.dayparts) {
        // Store the full configuration for time range lookups
        setFullDaypartConfig(data.data);
        
        // Convert dayparts object to array format expected by the dropdown
        const daypartsArray = Object.values(data.data.dayparts)
          .filter((dp: any) => dp.enabled)
          .map((dp: any) => ({
            key: dp.key,
            name: dp.name,
            startTime: dp.startTime,
            endTime: dp.endTime
          }));
        setDayparts(daypartsArray);
      } else {
        // Fallback to default dayparts if no configuration exists
        setDayparts([
          { key: 'BREAKFAST', name: 'Breakfast' },
          { key: 'LUNCH', name: 'Lunch' },
          { key: 'DINNER', name: 'Dinner' },
          { key: 'LATE_NIGHT', name: 'Late Night' }
        ]);
      }
    } catch (err) {
      console.error('Error loading dayparts:', err);
      // Fallback to default dayparts on error
      setDayparts([
        { key: 'BREAKFAST', name: 'Breakfast' },
        { key: 'LUNCH', name: 'Lunch' },
        { key: 'DINNER', name: 'Dinner' },
        { key: 'LATE_NIGHT', name: 'Late Night' }
      ]);
    }
  };

  const loadWeatherData = async () => {
    try {
      setLoading(true);
      setError(null);

      let url = `${API_ENDPOINTS.weatherTrends()}?days=${dateRange}`;
      if (selectedStore) url += `&storeId=${selectedStore}`;
      if (selectedDaypart) {
        url += `&daypart=${selectedDaypart}`;
        
        // Add time ranges if available from enterprise daypart configuration
        const selectedDaypartConfig = dayparts.find(dp => dp.key === selectedDaypart);
        if (selectedDaypartConfig?.startTime && selectedDaypartConfig?.endTime) {
          url += `&startTime=${selectedDaypartConfig.startTime}`;
          url += `&endTime=${selectedDaypartConfig.endTime}`;
        }
      }

      console.log('🌤️ Weather Data API URL:', url);
      const response = await authenticatedFetch(url);
      console.log('🌤️ Weather Data response status:', response.status);
      
      const data = await response.json();
      console.log('🌤️ Weather Data response:', JSON.stringify(data, null, 2));

      if (data.success) {
        setWeatherData(data.rawData || []);
        console.log('✅ Weather data loaded:', data.rawData?.length || 0, 'records');
        if (data.rawData?.length === 0) {
          console.log('⚠️ No weather records found. Response details:', {
            trends: data.trends,
            message: data.message,
            allKeys: Object.keys(data)
          });
        }
      } else {
        console.log('⚠️ Weather API error:', data.error);
        setError(data.error || 'Failed to load weather data');
      }
    } catch (err) {
      console.error('Error loading weather data:', err);
      setError('Failed to load weather data');
    } finally {
      setLoading(false);
    }
  };

  const loadTrends = async () => {
    try {
      setLoading(true);
      setError(null);

      let url = `${API_ENDPOINTS.weatherTrends()}?days=${dateRange}`;
      if (selectedStore) url += `&storeId=${selectedStore}`;
      if (selectedDaypart) {
        url += `&daypart=${selectedDaypart}`;
        
        // Add time ranges if available from enterprise daypart configuration
        const selectedDaypartConfig = dayparts.find(dp => dp.key === selectedDaypart);
        if (selectedDaypartConfig?.startTime && selectedDaypartConfig?.endTime) {
          url += `&startTime=${selectedDaypartConfig.startTime}`;
          url += `&endTime=${selectedDaypartConfig.endTime}`;
        }
      }

      const response = await authenticatedFetch(url);
      const data = await response.json();

      if (data.success) {
        console.log('Weather trends data received:', data.trends);
        console.log('weatherPatterns type:', typeof data.trends?.weatherPatterns);
        console.log('weatherPatterns value:', data.trends?.weatherPatterns);
        setTrends(data.trends);
      } else {
        setError(data.error || 'Failed to load weather trends');
      }
    } catch (err) {
      console.error('Error loading weather trends:', err);
      setError('Failed to load weather trends');
    } finally {
      setLoading(false);
    }
  };

  const collectWeatherData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Collecting weather data and 10-day forecast using AccuWeather API...');
      console.log('Available stores for weather collection:', stores.length);
      console.log('Store data:', stores.map(s => ({
        storeId: s.storeId,
        name: s.name,
        lat: s.latitude,
        lng: s.longitude
      })));

      if (stores.length === 0) {
        setError('No stores available for weather collection. Please ensure stores are loaded.');
        return;
      }

      // Collect both current weather and 10-day forecast in parallel
      const [currentWeatherResponse, forecastResponse] = await Promise.all([
        fetch(API_ENDPOINTS.weatherCollect(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({})
        }),
        fetch(API_ENDPOINTS.weatherCollectForecast(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({})
        })
      ]);

      const currentWeatherData = await currentWeatherResponse.json();
      const forecastData = await forecastResponse.json();

      console.log('Current weather collection response:', currentWeatherData);
      console.log('10-day forecast collection response:', forecastData);

      if (currentWeatherData.success && forecastData.success) {
        alert(`Successfully collected ${currentWeatherData.recordsCreated} current weather records and ${forecastData.recordsCreated} forecast records!`);
        loadWeatherData();
        return;
      } else {
        const errorMessage = !currentWeatherData.success
          ? `Current weather: ${currentWeatherData.error || 'Unknown error'}`
          : `Forecast: ${forecastData.error || 'Unknown error'}`;
        setError(errorMessage);
        return;
      }
    } catch (err) {
      console.error('Error collecting weather data:', err);
      setError('Failed to collect weather data');
    } finally {
      setLoading(false);
    }
  };

  // Parse hourly weather data into daypart records based on enterprise configuration
  const parseHourlyToDayparts = (hourlyData: any[]) => {
    const daypartRecords: any[] = [];
    
    if (!fullDaypartConfig?.dayparts) {
      console.log('⚠️ No daypart configuration available for parsing');
      return daypartRecords;
    }

    // Process each store's hourly data
    hourlyData.forEach(storeData => {
      const { storeId, storeName, location, historicalData } = storeData;
      
      // Group hourly data by dayparts
      Object.values(fullDaypartConfig.dayparts).forEach((daypart: any) => {
        if (!daypart.enabled) return;
        
        // Filter hours that belong to this daypart
        const daypartHours = historicalData.filter((hourData: any) => {
          // AccuWeather returns LocalObservationDateTime or DateTime
          const dateTime = new Date(hourData.LocalObservationDateTime || hourData.DateTime);
          const hourTime = dateTime.toTimeString().substring(0, 5); // Extract HH:MM
          return isTimeInDaypart(hourTime, daypart.startTime, daypart.endTime);
        });

        if (daypartHours.length > 0) {
          // Aggregate hourly data for this daypart
          const aggregated = aggregateAccuWeatherData(daypartHours, daypart);
          
          daypartRecords.push({
            weatherId: `${storeId}-${daypart.key}-${new Date().toISOString().split('T')[0]}`,
            storeId,
            storeName,
            date: new Date().toISOString().split('T')[0],
            daypart: daypart.name,
            daypartKey: daypart.key,
            weather: aggregated.weather,
            dailyForecast: aggregated.dailyForecast,
            location: location,
            metadata: {
              collectionTimestamp: new Date().toISOString(),
              source: 'accuweather-historical',
              hoursIncluded: daypartHours.length,
              daypartRange: `${daypart.startTime}-${daypart.endTime}`
            }
          });
        }
      });
    });

    return daypartRecords;
  };

  // Check if a time falls within a daypart range
  const isTimeInDaypart = (time: string, startTime: string, endTime: string): boolean => {
    const timeMinutes = timeToMinutes(time);
    const startMinutes = timeToMinutes(startTime);
    const endMinutes = timeToMinutes(endTime);

    if (startMinutes <= endMinutes) {
      // Normal range (e.g., 09:00-17:00)
      return timeMinutes >= startMinutes && timeMinutes < endMinutes;
    } else {
      // Overnight range (e.g., 22:00-06:00)
      return timeMinutes >= startMinutes || timeMinutes < endMinutes;
    }
  };

  // Convert HH:MM to minutes since midnight
  const timeToMinutes = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Aggregate AccuWeather hourly data for a daypart
  const aggregateAccuWeatherData = (hours: any[], daypart: any) => {
    if (!hours || hours.length === 0) {
      return {
        weather: {
          temperature: 0,
          weatherText: 'No Data',
          weatherIcon: 1,
          isDayTime: true,
          precipitationProbability: 0,
          precipitationAmount: 0,
          humidity: 0,
          windSpeed: 0,
          windDirection: 0,
          uvIndex: 0,
          visibility: 10
        },
        dailyForecast: {
          highTemperature: 0,
          lowTemperature: 0,
          dayWeatherText: 'No Data',
          nightWeatherText: 'No Data',
          precipitationProbability: 0,
          precipitationAmount: 0
        }
      };
    }

    const temperatures = hours.map(h => h.temperature || 0);
    const precipitationAmounts = hours.map(h => h.precipitationAmount || 0);
    const humidities = hours.map(h => h.humidity || 0);
    const windSpeeds = hours.map(h => h.windSpeed || 0);

    return {
      weather: {
        temperature: Math.round(temperatures.reduce((a, b) => a + b, 0) / temperatures.length),
        weatherText: getMostCommonWeather(hours.map(h => h.weatherText || 'Clear')),
        weatherIcon: hours[Math.floor(hours.length / 2)]?.weatherIcon || 1,
        isDayTime: hours.some(h => h.isDayTime),
        precipitationProbability: Math.max(...hours.map(h => h.precipitationProbability || 0)),
        precipitationAmount: precipitationAmounts.reduce((a, b) => a + b, 0),
        humidity: Math.round(humidities.reduce((a, b) => a + b, 0) / humidities.length),
        windSpeed: Math.round(windSpeeds.reduce((a, b) => a + b, 0) / windSpeeds.length),
        windDirection: hours[Math.floor(hours.length / 2)]?.windDirection || 0,
        uvIndex: Math.max(...hours.map(h => h.uvIndex || 0)),
        visibility: Math.min(...hours.map(h => h.visibility || 10))
      },
      dailyForecast: {
        highTemperature: Math.max(...temperatures),
        lowTemperature: Math.min(...temperatures),
        dayWeatherText: getMostCommonWeather(hours.map(h => h.weatherText || 'Clear')),
        nightWeatherText: getMostCommonWeather(hours.map(h => h.weatherText || 'Clear')),
        precipitationProbability: Math.max(...hours.map(h => h.precipitationProbability || 0)),
        precipitationAmount: precipitationAmounts.reduce((a, b) => a + b, 0)
      }
    };
  };

  // Aggregate hourly weather data for a daypart (legacy function)
  const aggregateHourlyData = (hours: any[], daypart: any) => {
    // Use the new AccuWeather aggregation function for consistency
    return aggregateAccuWeatherData(hours, daypart);
  };

  // Get the most common weather condition
  const getMostCommonWeather = (weatherTexts: string[]): string => {
    const counts: { [key: string]: number } = {};
    weatherTexts.forEach(text => {
      counts[text] = (counts[text] || 0) + 1;
    });
    return Object.keys(counts).reduce((a, b) => counts[a] > counts[b] ? a : b, 'Clear');
  };

  const getWeatherIcon = (weatherText: string) => {
    const text = weatherText.toLowerCase();
    if (text.includes('sunny') || text.includes('clear')) return '☀️';
    if (text.includes('cloudy') || text.includes('overcast')) return '☁️';
    if (text.includes('rain') || text.includes('shower')) return '🌧️';
    if (text.includes('snow')) return '❄️';
    if (text.includes('thunder') || text.includes('storm')) return '⛈️';
    if (text.includes('fog') || text.includes('mist')) return '🌫️';
    return '🌤️';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTemperature = (temp: number) => {
    return `${Math.round(temp)}°F`;
  };

  return (
    <div className="weather-dashboard">
      <div className="weather-header">
        <h2>Weather Data Management</h2>
        <p>Track weather conditions by daypart for sales trend analysis</p>
        <small style={{ color: '#6c757d' }}>
          Loaded {dayparts.length} dayparts for analysis
          {selectedDaypart && (() => {
            const selectedConfig = dayparts.find(dp => dp.key === selectedDaypart);
            return selectedConfig?.startTime && selectedConfig?.endTime 
              ? ` | Selected: ${selectedConfig.name} (${selectedConfig.startTime} - ${selectedConfig.endTime})`
              : ` | Selected: ${selectedConfig?.name || selectedDaypart}`;
          })()}
        </small>
      </div>

      {/* Filters */}
      <div className="weather-filters">
        <div className="filter-group">
          <label>Store:</label>
          <select 
            value={selectedStore} 
            onChange={(e) => setSelectedStore(e.target.value)}
          >
            <option value="">All Stores</option>
            {stores.map(store => (
              <option key={store.storeId} value={store.storeId}>
                {store.name} - {store.city}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Daypart:</label>
          <select 
            value={selectedDaypart} 
            onChange={(e) => setSelectedDaypart(e.target.value)}
          >
            <option value="">All Dayparts</option>
            {dayparts.map(daypart => (
              <option key={daypart.key} value={daypart.key}>
                {daypart.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Date Range:</label>
          <select 
            value={dateRange} 
            onChange={(e) => setDateRange(parseInt(e.target.value))}
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="weather-tabs">
        <button 
          className={`tab-button ${activeTab === 'data' ? 'active' : ''}`}
          onClick={() => setActiveTab('data')}
        >
          Weather Data
        </button>
        <button 
          className={`tab-button ${activeTab === 'trends' ? 'active' : ''}`}
          onClick={() => setActiveTab('trends')}
        >
          Trends Analysis
        </button>
        <button 
          className={`tab-button ${activeTab === 'collect' ? 'active' : ''}`}
          onClick={() => setActiveTab('collect')}
        >
          Data Collection
        </button>
      </div>

      {/* Content */}
      <div className="weather-content">
        {error && (
          <div className="error-message">
            <strong>Error:</strong> {error}
          </div>
        )}

        {loading && (
          <div className="loading-message">
            <div className="loading-spinner"></div>
            Loading weather data...
          </div>
        )}

        {activeTab === 'data' && !loading && (
          <div className="weather-data-tab">
            <div className="data-summary">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <div>
                  <h3>Weather Records ({weatherData.length})</h3>
                  <p>
                    Showing weather data for the last {dateRange} days
                    {selectedStore && ` for ${stores.find(s => s.storeId === selectedStore)?.name}`}
                    {selectedDaypart && ` during ${dayparts.find(d => d.key === selectedDaypart)?.name}`}
                  </p>
                </div>
                <button
                  className="collect-button"
                  onClick={collectWeatherData}
                  disabled={loading}
                  style={{ height: 'fit-content' }}
                >
                  Update Weather Data
                </button>
              </div>
              
              {/* Show forecast data when available */}
              {weatherData.length === 0 && (
                <div className="forecast-display">
                  <h4>📡 Live Weather Forecast Data</h4>
                  <p>No historical daypart records found. Showing current weather forecast data:</p>
                  
                  <div className="forecast-data-section">
                    {/* Fetch and display forecast data directly */}
                    <div className="forecast-note">
                      <strong>Note:</strong> This data shows current weather conditions and forecasts. 
                      Use "Collect Weather Data" to save daypart-specific records for trend analysis.
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="weather-table">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Store</th>
                    <th>Daypart</th>
                    <th>Weather</th>
                    <th>Temperature</th>
                    <th>High/Low</th>
                    <th>Precipitation</th>
                    <th>Humidity</th>
                    <th>Wind</th>
                    <th>Source</th>
                  </tr>
                </thead>
                <tbody>
                  {weatherData.map(record => (
                    <tr key={record.weatherId}>
                      <td>{formatDate(record.date)}</td>
                      <td>
                        <div className="store-info">
                          <strong>{record.storeName}</strong>
                          <small>{record.location.city}, {record.location.state}</small>
                        </div>
                      </td>
                      <td>
                        <span className={`daypart-badge ${record.daypartKey.toLowerCase()}`}>
                          {record.daypart}
                        </span>
                      </td>
                      <td>
                        <div className="weather-info">
                          <span className="weather-icon">{getWeatherIcon(record.weather.weatherText)}</span>
                          <span>{record.weather.weatherText}</span>
                        </div>
                      </td>
                      <td className="temperature">
                        {formatTemperature(record.weather.temperature)}
                      </td>
                      <td className="temp-range">
                        {formatTemperature(record.dailyForecast.highTemperature)} / {formatTemperature(record.dailyForecast.lowTemperature)}
                      </td>
                      <td className="precipitation">
                        {record.weather.precipitationAmount > 0 ? 
                          `${record.weather.precipitationAmount.toFixed(2)}"` : 
                          '-'
                        }
                        {record.weather.precipitationProbability > 30 && (
                          <div className="precip-chance">({record.weather.precipitationProbability}%)</div>
                        )}
                      </td>
                      <td>{record.weather.humidity}%</td>
                      <td>{Math.round(record.weather.windSpeed)} mph</td>
                      <td>
                        <span className={`source-badge ${record.metadata?.source?.replace('-', '')}`}>
                          {record.metadata?.source || 'historical'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'trends' && !loading && trends && (
          <div className="weather-trends-tab">
            <div className="trends-summary">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <div>
                  <h3>Weather Trends Analysis</h3>
                  <p>{trends.summary || 'No trends data available'}</p>
                </div>
                <button
                  className="collect-button"
                  onClick={collectWeatherData}
                  disabled={loading}
                  style={{ height: 'fit-content' }}
                >
                  Update Weather Data
                </button>
              </div>
            </div>

            <div className="trends-grid">
              <div className="trend-card">
                <h4>Weather Patterns</h4>
                <div className="weather-patterns">
                  {Array.isArray(trends.weatherPatterns) ? trends.weatherPatterns.map((pattern, index) => (
                    <div key={index} className="pattern-item">
                      <span className="weather-icon">{getWeatherIcon(pattern.condition)}</span>
                      <div className="pattern-details">
                        <strong>{pattern.condition}</strong>
                        <div className="pattern-stats">
                          {pattern.count} days ({pattern.percentage}%)
                        </div>
                      </div>
                    </div>
                  )) : <div className="no-data">No weather pattern data available</div>}
                </div>
              </div>

              <div className="trend-card">
                <h4>Temperature Analysis</h4>
                <div className="temperature-stats">
                  <div className="stat-item">
                    <label>Average:</label>
                    <span>{formatTemperature(trends.temperatureAnalysis?.average || 0)}</span>
                  </div>
                  <div className="stat-item">
                    <label>Maximum:</label>
                    <span>{formatTemperature(trends.temperatureAnalysis?.maximum || 0)}</span>
                  </div>
                  <div className="stat-item">
                    <label>Minimum:</label>
                    <span>{formatTemperature(trends.temperatureAnalysis?.minimum || 0)}</span>
                  </div>
                  <div className="stat-item">
                    <label>Range:</label>
                    <span>{trends.temperatureAnalysis?.range || 0}°F</span>
                  </div>
                </div>
              </div>

              <div className="trend-card">
                <h4>Precipitation Analysis</h4>
                <div className="precipitation-stats">
                  <div className="stat-item">
                    <label>Days with Precipitation:</label>
                    <span>{trends.precipitationAnalysis?.totalDaysWithPrecipitation || 0}</span>
                  </div>
                  <div className="stat-item">
                    <label>Precipitation Percentage:</label>
                    <span>{trends.precipitationAnalysis?.precipitationPercentage || 0}%</span>
                  </div>
                  <div className="stat-item">
                    <label>Average Amount:</label>
                    <span>{trends.precipitationAnalysis?.averagePrecipitationAmount || 0}"</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'collect' && (
          <div className="weather-collect-tab">
            <div className="collect-info">
              <h3>Weather Data Collection</h3>
              <p>Collect current weather data for all store locations across all dayparts.</p>
              
              <div className="collection-details">
                <h4>What gets collected:</h4>
                <ul>
                  <li><strong>Current Conditions:</strong> Temperature, weather description, humidity, wind</li>
                  <li><strong>Daily Forecast:</strong> High/low temperatures, precipitation probability</li>
                  <li><strong>Daypart Data:</strong> Breakfast, Lunch, Dinner, Late Night records</li>
                  <li><strong>Location Data:</strong> Store-specific weather based on coordinates</li>
                </ul>
              </div>

              <div className="daypart-info">
                <h4>Daypart Schedule:</h4>
                <div className="daypart-schedule">
                  <div className="daypart-item">
                    <strong>Breakfast:</strong> 6:00 AM - 11:00 AM
                  </div>
                  <div className="daypart-item">
                    <strong>Lunch:</strong> 11:00 AM - 4:00 PM
                  </div>
                  <div className="daypart-item">
                    <strong>Dinner:</strong> 4:00 PM - 10:00 PM
                  </div>
                  <div className="daypart-item">
                    <strong>Late Night:</strong> 10:00 PM - 6:00 AM
                  </div>
                </div>
              </div>

              <button 
                className="collect-button"
                onClick={collectWeatherData}
                disabled={loading}
              >
                {loading ? 'Collecting Weather Data...' : 'Collect Weather Data Now'}
              </button>

              <div className="collection-note">
                <p><strong>Note:</strong> This will collect weather data for today across all store locations. 
                Data collection should typically be run once daily to maintain accurate records for trend analysis.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WeatherDashboard;